/**
 * CategoryRow.tsx — 8208-A
 *
 * Single category header + accordion body.
 * Collapsed: shows icon + name + dropdownDescription.
 * Expanded: also shows sub-type checkboxes.
 */
import React from "react";
import styled from "styled-components";
import SVG from "react-inlinesvg";
import classnames from "classnames";
import { FeedbackCategory, FeedbackPlugin, getCategoryIconPath } from "../../../shared/feedbackCategories";
import { SubTypeCheckbox } from "./SubTypeCheckbox";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CategoryRowProps {
  category: FeedbackCategory;
  plugins: FeedbackPlugin[];
  isExpanded: boolean;
  isSelected: boolean;
  onToggleExpand: () => void;
  onPluginToggle: (pluginId: string, currentEnabled: boolean) => void;
  onSelect: (categoryKey: string) => void;
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const RowContainer = styled.div`
  border-radius: 8px;

  &.category-row-selected {
    box-shadow: inset 0 0 0 2px var(--primary-cta);
  }
`;

const CategoryHeader = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  cursor: pointer;
  user-select: none;
  border-radius: 8px;

  &:hover {
    background-color: ${({ $isExpanded }) => ($isExpanded ? "transparent" : "var(--menu-color-hover)")};
  }
`;

const CategoryIconWrapper = styled.div`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--grey-0);

  svg,
  img {
    width: 32px;
    height: 32px;
  }
`;

const CategoryText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

const CategoryName = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: var(--grey-0);
  line-height: 1.3;
`;

const CategoryDesc = styled.div`
  font-size: 14px;
  color: var(--grey-0);
  line-height: 1.3;
`;

const SubTypesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding-bottom: 16px;
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CategoryRow({
  category,
  plugins,
  isExpanded,
  isSelected,
  onToggleExpand,
  onPluginToggle,
  onSelect,
}: CategoryRowProps): JSX.Element {
  const iconPath = getCategoryIconPath(category.icon);

  const handleClick = () => {
    onToggleExpand();
    onSelect(category.key);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleClick();
    }
  };

  return (
    <RowContainer
      className={classnames("category-row", { "category-row-selected": isSelected })}
    >
      <CategoryHeader
        className="category-header"
        $isExpanded={isExpanded}
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <CategoryIconWrapper>
          <SVG src={iconPath} aria-hidden="true" />
        </CategoryIconWrapper>
        <CategoryText className="category-text">
          <CategoryName className="category-name">{category.name}</CategoryName>
          <CategoryDesc className="category-desc">{category.dropdownDescription}</CategoryDesc>
        </CategoryText>
      </CategoryHeader>

      {isExpanded && plugins.length > 0 && (() => {
        // SW-9063 — every category must keep at least one enabled sub-type.
        // Compute the count once and mark the sole enabled plugin's row as
        // `isLastEnabled` so its checkbox locks against off-toggle.
        const enabledCount = plugins.reduce(
          (acc, p) => (p.enabled ? acc + 1 : acc),
          0,
        );
        return (
          <SubTypesContainer className="category-subtypes">
            {plugins.map((plugin) => (
              <SubTypeCheckbox
                key={plugin.id}
                plugin={plugin}
                onToggle={onPluginToggle}
                isLastEnabled={plugin.enabled && enabledCount === 1}
              />
            ))}
          </SubTypesContainer>
        );
      })()}
    </RowContainer>
  );
}

export default CategoryRow;
